/**
 * FILE: src/layouts/settings/index.jsx
 * 
 * Settings Page — Full page version of the Configurator sidebar
 * 
 * Features:
 * - Language selection
 * - Theme mode (light/dark)
 * - Sidenav style (dark/transparent/white)
 * - Navbar fixed toggle
 * - Future: More settings sections can be added
 */

import { useState } from "react";
import {
  Card,
  Grid,
  Switch,
  Divider,
  Select,
  MenuItem,
  FormControl,
  Box,
  IconButton,
  Collapse,
} from "@mui/material";
import LanguageIcon from "@mui/icons-material/Language";
import PaletteIcon from "@mui/icons-material/Palette";
import ViewSidebarIcon from "@mui/icons-material/ViewSidebar";
import NavigationIcon from "@mui/icons-material/Navigation";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";

import KDBox from "components/KDBox";
import KDTypography from "components/KDTypography";
import KDButton from "components/KDButton";
import DashboardNavbar from "components/DashboardNavbar";
import {
  useMaterialUIController,
  setTransparentSidenav,
  setWhiteSidenav,
  setFixedNavbar,
  setDarkMode,
} from "context";
import { useI18n, useT } from "context/I18nContext";

// ============================================================================
// STYLES - Theme-aware
// ============================================================================

const getCardStyle = (darkMode) => ({
  background: darkMode ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.02)",
  backdropFilter: "blur(10px)",
  border: `1px solid ${darkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)"}`,
  borderRadius: "16px",
  overflow: "hidden",
});

const getSectionHeaderStyle = (darkMode) => ({
  p: 2,
  display: "flex",
  alignItems: "center",
  gap: 1.5,
  cursor: "pointer",
  "&:hover": { background: darkMode ? "rgba(255, 255, 255, 0.02)" : "rgba(0, 0, 0, 0.02)" },
});

const getLabelStyle = (darkMode) => ({
  color: darkMode ? "rgba(255, 255, 255, 0.6)" : "rgba(0, 0, 0, 0.5)",
  fontSize: "0.75rem",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  mb: 0.5,
});

// ============================================================================
// SETTINGS SECTION COMPONENT
// ============================================================================

function SettingsSection({ icon: IconComponent, title, description, color, expanded, onToggle, children, darkMode }) {
  const cardStyle = getCardStyle(darkMode);
  const sectionHeaderStyle = getSectionHeaderStyle(darkMode);
  const textColor = darkMode ? "white" : "dark";
  
  return (
    <Card sx={{ ...cardStyle, mb: 2 }}>
      <Box sx={sectionHeaderStyle} onClick={onToggle}>
        <Box
          sx={{
            background: `linear-gradient(135deg, ${color}40 0%, ${color}20 100%)`,
            borderRadius: "10px",
            p: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <IconComponent sx={{ color, fontSize: 24 }} />
        </Box>
        <KDBox flex={1}>
          <KDTypography variant="h6" color={textColor} fontWeight="bold">
            {title}
          </KDTypography>
          {description && (
            <KDTypography variant="caption" color="text" sx={{ opacity: 0.6 }}>
              {description}
            </KDTypography>
          )}
        </KDBox>
        <IconButton size="small" sx={{ color: darkMode ? "rgba(255, 255, 255, 0.5)" : "rgba(0, 0, 0, 0.5)" }}>
          <ExpandMoreIcon
            sx={{ transform: expanded ? "rotate(180deg)" : "rotate(0)", transition: "0.3s" }}
          />
        </IconButton>
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ p: 3, pt: 0 }}>
          {children}
        </Box>
      </Collapse>
    </Card>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function Settings() {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, fixedNavbar, transparentSidenav, whiteSidenav, darkMode } = controller;
  
  const { locale, setLocale, supportedLocales, localeInfo } = useI18n();
  const t = useT();
  
  // Section expansion state
  const [expandedSections, setExpandedSections] = useState({
    language: true,
    appearance: true,
    sidenav: false,
    navbar: false,
  });
  
  const toggleSection = (section) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };
  
  // Handlers
  const handleTransparentSidenav = () => {
    setTransparentSidenav(dispatch, true);
    setWhiteSidenav(dispatch, false);
  };
  const handleWhiteSidenav = () => {
    setWhiteSidenav(dispatch, true);
    setTransparentSidenav(dispatch, false);
  };
  const handleDarkSidenav = () => {
    setWhiteSidenav(dispatch, false);
    setTransparentSidenav(dispatch, false);
  };
  const handleFixedNavbar = () => setFixedNavbar(dispatch, !fixedNavbar);
  
  // Get current sidenav type
  const getSidenavType = () => {
    if (whiteSidenav) return "white";
    if (transparentSidenav) return "transparent";
    return "dark";
  };
  
  // Theme-aware colors
  const textColor = darkMode ? "white" : "dark";
  const labelStyle = getLabelStyle(darkMode);
  
  // Button styles for sidenav type - theme-aware
  const getButtonStyle = (type, isActive) => ({
    flex: 1,
    py: 1.5,
    borderRadius: "10px",
    border: "1px solid",
    borderColor: isActive ? "#0D9488" : darkMode ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.15)",
    bgcolor: isActive ? "rgba(13, 148, 136, 0.2)" : "transparent",
    color: isActive ? "#0D9488" : darkMode ? "rgba(255, 255, 255, 0.6)" : "rgba(0, 0, 0, 0.6)",
    "&:hover": {
      bgcolor: isActive ? "rgba(13, 148, 136, 0.3)" : darkMode ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.04)",
      borderColor: isActive ? "#0D9488" : darkMode ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 0, 0, 0.25)",
    },
  });
  
  return (
    <KDBox
      sx={({ breakpoints, transitions }) => ({
        p: 3,
        marginLeft: miniSidenav ? "80px" : "260px",
        transition: transitions.create("margin-left", {
          easing: transitions.easing.easeInOut,
          duration: transitions.duration.standard,
        }),
        [breakpoints.down("xl")]: { marginLeft: 0 },
      })}
    >
      <DashboardNavbar />
      
      <KDBox mt={4}>
        {/* Header */}
        <KDBox mb={3}>
          <KDTypography variant="h4" color={textColor} fontWeight="bold">
            {t("settings.title")}
          </KDTypography>
          <KDTypography variant="body2" color="text" sx={{ opacity: 0.7 }}>
            {t("settings.description")}
          </KDTypography>
        </KDBox>
        
        <Grid container spacing={3}>
          <Grid item xs={12} lg={8}>
            {/* Language Section */}
            <SettingsSection
              icon={LanguageIcon}
              title={t("settings.language")}
              description={t("settings.languageDescription")}
              darkMode={darkMode}
              color="#3B82F6"
              expanded={expandedSections.language}
              onToggle={() => toggleSection("language")}
            >
              <FormControl fullWidth size="small">
                <KDTypography sx={labelStyle}>{t("settings.selectLanguage")}</KDTypography>
                <Select
                  value={locale || "en"}
                  onChange={(e) => setLocale(e.target.value)}
                  renderValue={(selected) => {
                    const info = localeInfo?.[selected];
                    return info ? info.nativeName : selected;
                  }}
                  sx={{
                    backgroundColor: darkMode ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.02)",
                    color: darkMode ? "#fff" : "inherit",
                    borderRadius: "10px",
                    "& .MuiSelect-icon": { color: darkMode ? "#fff" : "inherit" },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: darkMode ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.15)",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(13, 148, 136, 0.5)",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#0D9488",
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        bgcolor: darkMode ? "rgba(26, 26, 46, 0.98)" : "#fff",
                        border: `1px solid ${darkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"}`,
                        borderRadius: "8px",
                        "& .MuiMenuItem-root": {
                          color: darkMode ? "white" : "inherit",
                          "&:hover": { bgcolor: "rgba(13, 148, 136, 0.2)" },
                        },
                      },
                    },
                  }}
                >
                  {supportedLocales.map((code) => {
                    const info = localeInfo?.[code];
                    return (
                      <MenuItem key={code} value={code}>
                        {info ? `${info.nativeName} (${info.name})` : code}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </SettingsSection>
            
            {/* Appearance Section */}
            <SettingsSection
              icon={PaletteIcon}
              title={t("settings.appearance")}
              description={t("settings.appearanceDescription")}
              color="#A78BFA"
              expanded={expandedSections.appearance}
              onToggle={() => toggleSection("appearance")}
              darkMode={darkMode}
            >
              <KDBox>
                <KDTypography sx={labelStyle}>{t("settings.themeMode")}</KDTypography>
                <KDBox display="flex" gap={2} mt={1}>
                  <KDButton
                    variant="outlined"
                    onClick={() => setDarkMode(dispatch, false)}
                    sx={getButtonStyle("light", !darkMode)}
                    startIcon={<LightModeIcon />}
                  >
                    {t("settings.light")}
                  </KDButton>
                  <KDButton
                    variant="outlined"
                    onClick={() => setDarkMode(dispatch, true)}
                    sx={getButtonStyle("dark", darkMode)}
                    startIcon={<DarkModeIcon />}
                  >
                    {t("settings.dark")}
                  </KDButton>
                </KDBox>
              </KDBox>
            </SettingsSection>
            
            {/* Sidenav Section */}
            <SettingsSection
              icon={ViewSidebarIcon}
              title={t("settings.sidenavType")}
              description={t("settings.sidenavDescription")}
              color="#F59E0B"
              expanded={expandedSections.sidenav}
              onToggle={() => toggleSection("sidenav")}
              darkMode={darkMode}
            >
              <KDBox>
                <KDTypography sx={labelStyle}>{t("settings.sidebarStyle")}</KDTypography>
                <KDBox display="flex" gap={2} mt={1}>
                  <KDButton
                    variant="outlined"
                    onClick={handleDarkSidenav}
                    sx={getButtonStyle("dark", getSidenavType() === "dark")}
                  >
                    {t("settings.dark")}
                  </KDButton>
                  <KDButton
                    variant="outlined"
                    onClick={handleTransparentSidenav}
                    sx={getButtonStyle("transparent", getSidenavType() === "transparent")}
                  >
                    {t("settings.transparent")}
                  </KDButton>
                  <KDButton
                    variant="outlined"
                    onClick={handleWhiteSidenav}
                    sx={getButtonStyle("white", getSidenavType() === "white")}
                  >
                    {t("settings.white")}
                  </KDButton>
                </KDBox>
              </KDBox>
            </SettingsSection>
            
            {/* Navbar Section */}
            <SettingsSection
              icon={NavigationIcon}
              title={t("settings.navigation")}
              description={t("settings.navigationDescription")}
              color="#10B981"
              expanded={expandedSections.navbar}
              onToggle={() => toggleSection("navbar")}
              darkMode={darkMode}
            >
              <KDBox display="flex" justifyContent="space-between" alignItems="center">
                <KDBox>
                  <KDTypography variant="body2" color={textColor}>
                    {t("settings.fixedNavbar")}
                  </KDTypography>
                  <KDTypography variant="caption" color="text" sx={{ opacity: 0.6 }}>
                    {t("settings.fixedNavbarDescription")}
                  </KDTypography>
                </KDBox>
                <Switch
                  checked={fixedNavbar}
                  onChange={handleFixedNavbar}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: "#0D9488" },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#0D9488",
                    },
                  }}
                />
              </KDBox>
            </SettingsSection>
          </Grid>
          
          {/* Right sidebar - future placeholders */}
          <Grid item xs={12} lg={4}>
            <Card sx={{ ...getCardStyle(darkMode), p: 3 }}>
              <KDTypography variant="h6" color={textColor} fontWeight="bold" mb={2}>
                {t("settings.quickTips")}
              </KDTypography>
              <Divider sx={{ borderColor: darkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)", mb: 2 }} />
              
              <KDBox mb={2}>
                <KDTypography variant="body2" color="text" sx={{ opacity: 0.8 }}>
                  • {t("settings.tipDarkMode")}
                </KDTypography>
              </KDBox>
              <KDBox mb={2}>
                <KDTypography variant="body2" color="text" sx={{ opacity: 0.8 }}>
                  • {t("settings.tipTransparent")}
                </KDTypography>
              </KDBox>
              <KDBox mb={2}>
                <KDTypography variant="body2" color="text" sx={{ opacity: 0.8 }}>
                  • {t("settings.tipSaved")}
                </KDTypography>
              </KDBox>
              
              <Divider sx={{ borderColor: darkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)", my: 2 }} />
              
              <KDTypography variant="caption" color="text" sx={{ opacity: 0.5 }}>
                {t("settings.moreComingSoon")}
              </KDTypography>
            </Card>
          </Grid>
        </Grid>
      </KDBox>
    </KDBox>
  );
}
